// One thing waiting for a person (D3, D147): a drafted message to approve, edit or reject, with the
// judge's score on a pseudonymised copy (D141); an agent step's result to approve or correct; or a
// task, where ops pick how the case goes on.
import { useState } from "react";

import { send } from "./api";
import { TemplatePicker } from "./Templates";
import type { Approval } from "./types";

function Score({ judge }: { judge: Approval["judge"] }) {
  if (!judge) {
    return null;
  }
  if (judge.problem) {
    return <p className="note warn">{judge.problem}</p>;
  }
  const pass = judge.verdict === "pass";
  return (
    <p className="note">
      <span className={pass ? "badge ok" : "badge warn"}>
        Judge {judge.score ?? "?"}/5 {pass ? "pass" : "fail"}
      </span>{" "}
      {judge.reasons}
    </p>
  );
}

export function ApprovalCard({ approval, onDone }: { approval: Approval; onDone: () => void }) {
  const [text, setText] = useState(approval.draft);
  const [note, setNote] = useState("");
  const [choice, setChoice] = useState(approval.proposal.branch ?? approval.proposal.options?.[0]?.to ?? "");
  const [fields, setFields] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(approval.proposal.fields ?? {}).map(([k, v]) => [k, String(v)])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const decide = async (verdict: "approve" | "edit" | "reject", body: Record<string, unknown> = {}) => {
    setBusy(true);
    setError("");
    try {
      await send(`/api/approvals/${approval.id}/decision`, { verdict, note, ...body });
      onDone();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const options = approval.proposal.options ?? [];
  const edited = text !== approval.draft;
  const closed = approval.window_open === false;
  const original = approval.proposal.fields ?? {};
  const corrected =
    choice !== (approval.proposal.branch ?? choice) || Object.entries(fields).some(([k, v]) => String(original[k] ?? "") !== v);
  return (
    <div className="approval">
      <div className="approval-head">
        <strong>{approval.display_name ?? "Case"}</strong> <span className="muted">{approval.step}</span>
        <span className="badge">{approval.kind === "message" ? "Message" : approval.kind === "step" ? "Step result" : "Task"}</span>
      </div>
      {approval.kind === "message" && closed && (
        <>
          <p className="note warn">
            More than 24 hours have passed since the member's last message, so WhatsApp only lets an approved template through. The agent's draft, for
            reference:
          </p>
          <p className="preview">{approval.draft}</p>
          <TemplatePicker member={approval.display_name ?? ""} busy={busy} onSend={(template) => decide("edit", { template })} />
        </>
      )}
      {approval.kind === "message" && !closed && (
        <>
          <textarea value={text} rows={Math.min(10, Math.max(3, text.split("\n").length + 1))} onChange={(e) => setText(e.target.value)} />
          <Score judge={approval.judge} />
        </>
      )}
      {approval.kind === "step" && (
        <>
          {approval.proposal.summary && <p>{approval.proposal.summary}</p>}
          <div className="fields">
            {Object.keys(fields).length === 0 && <p className="muted">No details extracted.</p>}
            {Object.entries(fields).map(([k, v]) => (
              <label key={k}>
                {k}
                <input value={v} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} />
              </label>
            ))}
          </div>
          {(approval.proposal.missing ?? []).length > 0 && <p className="note">Missing: {approval.proposal.missing!.join(", ")}</p>}
          <Score judge={approval.judge} />
        </>
      )}
      {approval.kind === "task" && approval.proposal.instructions && <p>{approval.proposal.instructions}</p>}
      {options.length > 0 && approval.kind !== "message" && (
        <label>
          Next
          <select value={choice} onChange={(e) => setChoice(e.target.value)}>
            {options.map((o) => (
              <option key={o.to} value={o.to}>
                {o.to}
                {o.when ? `: ${o.when}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      <input placeholder="Note (why, for a rejection)" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="actions">
        {approval.kind === "message" && closed ? null : approval.kind === "message" ? (
          <button disabled={busy || !text.trim()} onClick={() => (edited ? decide("edit", { text }) : decide("approve"))}>
            {edited ? "Send edited" : "Approve and send"}
          </button>
        ) : (
          <button disabled={busy} onClick={() => decide(approval.kind === "step" && corrected ? "edit" : "approve", { choice, fields })}>
            {corrected ? "Approve with changes" : "Approve"}
          </button>
        )}
        <button className="secondary" disabled={busy} onClick={() => decide("reject")}>
          Reject
        </button>
      </div>
      {error && <p className="note warn">{error}</p>}
    </div>
  );
}
